import React, { useState, useEffect, useMemo } from "react";
import Dropdown, { Option } from "../../../components/customer/Dropdown";
import TextAreaComp from "../../../components/customer/TextAreaComp";
import TextInputComp from "../../../components/customer/TextInputComp";
import MediaUploadComp from "../../../components/MediaUploadComp";
import { useNavigate } from "react-router-dom";
import CommonButton from "../../../components/CommonButton";
import { useCreatePetMutation } from "../../../redux/actions/Admin/PetsAdmin";

import { ICreatePetRequest, PetSellType, PetStatus } from "../../../types/Request/AdminRequest";
import { ArrowLeft, Loader2, Plus, Trash2, Search, Check, FolderTree } from "lucide-react";
import { useUploadFileMutation } from "../../../redux/actions/Admin/ServiceAdmin";
import {
  useLazyGetCategoryQuery,
  useLazyGetSystemDefinedCategoryQuery,
} from "../../../redux/actions/Admin/dashboardAdmin";

interface IVideoAttachment {
  videoUrl: string;
  thumbnailUrl: string;
}

interface ICategoryNode {
  id: number;
  name: string;
  slug?: string;
  parentId?: number | null;
  children?: ICategoryNode[];
  subCategories?: ICategoryNode[];
}

const AdminPetAddScreen: React.FC = () => {
  const navigate = useNavigate();
  const [createPet, { isLoading: isCreating }] = useCreatePetMutation();
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();

  // Category APIs
  const [
    triggerGetFullTree,
    { data: fullTreeResponse, isLoading: isLoadingFullTree },
  ] = useLazyGetCategoryQuery();

  // Same Tier-1/system-category source used by Category Management.
  // We use it to locate the Pets branch instead of showing the complete
  // category tree.
  const [
    triggerGetSystemTabs,
    { data: systemTabs, isLoading: isLoadingSystemTabs },
  ] = useLazyGetSystemDefinedCategoryQuery();

  useEffect(() => {
    triggerGetSystemTabs();
    triggerGetFullTree();
  }, [triggerGetSystemTabs, triggerGetFullTree]);

  // --- State Management ---
  const [microchipId, setMicrochipId] = useState("");
  const [petName, setPetName] = useState("");
  const [breed, setBreed] = useState("");
  const [birthDate, setBirthDate] = useState("");

  const [gender, setGender] = useState<Option | null>(null);
  const [description, setDescription] = useState("");
  const [tag, setTag] = useState("");
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const [weight, setWeight] = useState("");

  // Category Tree Selection States
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [selectedCategoryLabels, setSelectedCategoryLabels] = useState<string[]>([]);
  const [categorySearchQuery, setCategorySearchQuery] = useState("");

  // Checkbox toggles
  const [isVaccinated, setIsVaccinated] = useState(true);
  const [vaccinationDetails, setVaccinationDetails] = useState("");
  const [isKCIRegistered, setIsKCIRegistered] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);

  // Pricing States
  const [price, setPrice] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [bookPrice, setBookPrice] = useState("");

  // Media States
  const [mainImage, setMainImage] = useState("");
  const [subImages, setSubImages] = useState<string[]>([]);
  const [videos, setVideos] = useState<IVideoAttachment[]>([]);

  const [petType, setPetType] = useState<Option | null>(null);
  const [petStatus, setPetStatus] = useState<Option | null>(null);

  const genderOptions: Option[] = [
    { id: "g1", label: "Male", value: "male" },
    { id: "g2", label: "Female", value: "female" },
  ];

  const typeOptions: Option[] = [
    { id: "t1", label: "Buy Pet", value: String(PetSellType.Sell) },
    { id: "t2", label: "Adopt Pet", value: String(PetSellType.Adopt) },
  ];

  const statusOptions: Option[] = [
    { id: "s1", label: "Available", value: String(PetStatus.Available) },
    { id: "s2", label: "Reserved", value: String(PetStatus.Reserved) },
    { id: "s3", label: "Sold", value: String(PetStatus.Sold) },
    { id: "s4", label: "Adopted", value: String(PetStatus.Adopted) },
  ];

  // Category Selection Toggle Handler
  const handleToggleCategorySelection = (catId: number, catName: string) => {
    setSelectedCategoryIds((prev) => {
      const exists = prev.includes(catId);
      if (exists) {
        setSelectedCategoryLabels((lbls) => lbls.filter((l) => l !== catName));
        return prev.filter((id) => id !== catId);
      } else {
        setSelectedCategoryLabels((lbls) => [...lbls, catName]);
        return [...prev, catId];
      }
    });
  };

  // Extract raw category tree safely.
  const treeList: ICategoryNode[] = useMemo(() => {
    const raw =
      (fullTreeResponse as any)?.data ||
      (fullTreeResponse as any)?.value ||
      fullTreeResponse ||
      [];

    return Array.isArray(raw) ? raw : [];
  }, [fullTreeResponse]);

  // ---------------------------------------------------------------
  // PET-ONLY CATEGORY TREE
  // ---------------------------------------------------------------
  // Category Management has the Tier-1/system category tabs. Find
  // the Pets tab there, then keep only that branch from the full tree.
  // This prevents Food, Services, Accessories, etc. from appearing
  // in the Add Pet tagging selector.
  const petCategoryTree: ICategoryNode[] = useMemo(() => {
    if (!treeList.length) return [];

    const tabs = Array.isArray(systemTabs) ? systemTabs : [];

    const petsTab = tabs.find(
      (tab: any) =>
        String(tab?.name || "").trim().toLowerCase() === "pets"
    );

    if (!petsTab) {
      return [];
    }

    const petsRootId = Number(petsTab.id);

    // If the full tree contains the Pets root, use it and all of its
    // descendants. Otherwise, use direct children whose parent is Pets.
    const petsRoot = treeList.find(
      (node) => Number(node.id) === petsRootId
    );

    if (petsRoot) {
      return [petsRoot];
    }

    const directPetChildren = treeList.filter(
      (node) => Number(node.parentId) === petsRootId
    );

    return directPetChildren;
  }, [treeList, systemTabs]);

  // Recursive Tree Node Renderer with Search Highlighting
  const renderHierarchicalTreeRows = (nodes: ICategoryNode[], depth = 0): React.ReactNode => {
    return nodes.map((node) => {
      const childNodes = node.children || node.subCategories || [];
      const isSelected = selectedCategoryIds.includes(node.id);
      const isMatchingSearch =
        !categorySearchQuery ||
        node.name.toLowerCase().includes(categorySearchQuery.toLowerCase());

      return (
        <div key={node.id} className="space-y-1">
          {isMatchingSearch && (
            <div
              onClick={() => handleToggleCategorySelection(node.id, node.name)}
              style={{ paddingLeft: `${depth * 16 + 8}px` }}
              className={`flex items-center justify-between py-1.5 px-2 rounded-lg cursor-pointer transition text-xs select-none ${
                isSelected
                  ? "bg-emerald-50 text-emerald-800 font-bold border border-emerald-200"
                  : "hover:bg-slate-100 text-slate-700"
              }`}
            >
              <div className="flex items-center gap-2">
                <div
                  className={`w-4 h-4 rounded flex items-center justify-center border ${
                    isSelected
                      ? "bg-[#2F5E2E] border-[#2F5E2E] text-white"
                      : "border-slate-300 bg-white"
                  }`}
                >
                  {isSelected && <Check size={12} strokeWidth={3} />}
                </div>
                <span>{node.name}</span>
              </div>
              
            </div>
          )}
          {childNodes.length > 0 && renderHierarchicalTreeRows(childNodes, depth + 1)}
        </div>
      );
    });
  };

  const handleMediaUpload = async (file: File, folderName: string): Promise<string | null> => {
    try {
      const response = await uploadFile({
        File: file,
        FolderName: folderName,
      }).unwrap();
      return response.fileUrl;
    } catch (error) {
      console.error(`File upload failed [${folderName}]:`, error);
      alert("File upload failed. Please try again.");
      return null;
    }
  };

  const handleMainImageUpload = async (file: File) => {
    const url = await handleMediaUpload(file, "Pets/Primary");
    if (url) setMainImage(url);
  };

  const handleAddSubImageSlot = () => {
    setSubImages((prev) => [...prev, ""]);
  };

  const handleSubImageUploadAtIndex = async (file: File, index: number) => {
    const url = await handleMediaUpload(file, "Pets/Gallery");
    if (url) {
      setSubImages((prev) => {
        const updated = [...prev];
        updated[index] = url;
        return updated;
      });
    }
  };

  const handleRemoveSubImageSlot = (index: number) => {
    setSubImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddVideoSlot = () => {
    setVideos((prev) => [...prev, { videoUrl: "", thumbnailUrl: "" }]);
  };

  const handleVideoUploadAtIndex = async (file: File, index: number) => {
    const url = await handleMediaUpload(file, "Pets/Videos");
    if (url) {
      setVideos((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          videoUrl: url,
          thumbnailUrl: mainImage || url,
        };
        return updated;
      });
    }
  };

  const handleRemoveVideoSlot = (index: number) => {
    setVideos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFormSubmit = async () => {
    if (
      !petName ||
      selectedCategoryIds.length === 0 ||
      !breed ||
      !price ||
      !description ||
      !birthDate
    ) {
      alert("Please fill in all required fields and select at least one Category.");
      return;
    }

    const payload: ICreatePetRequest | any = {
      microchipId: microchipId.trim(),
      category: selectedCategoryLabels[0] || "",
      categoryIds: selectedCategoryIds,
      petName: petName.trim(),
      breed: breed.trim(),
      price: Number(price) || 0,
      salePrice: salePrice ? Number(salePrice) : 0,
      bookPrice: bookPrice ? Number(bookPrice) : 0,
      productImage: mainImage,
      description: description,
      age: birthDate,
      gender: gender ? gender.label : "",
      tag: tag.trim(),
      petType: petType ? Number(petType.value) : PetSellType.Sell,
      petStatus: petStatus ? Number(petStatus.value) : PetStatus.Available,
      color: color || "Default",
      size: size || "Medium",
      weight: weight ? Number(weight) : 0,
      isVaccinated: isVaccinated,
      vaccinationDetails: vaccinationDetails,
      isKCIRegistered: isKCIRegistered,
      isFeatured: isFeatured,
      subImages: subImages.filter(Boolean),
      videos: videos.filter((v) => v.videoUrl),
    };

    try {
      await createPet(payload).unwrap();
      alert("Pet added successfully!");
      navigate(-1);
    } catch (err) {
      console.error("Transmission fault adding pet instance payload:", err);
      alert("Failed to add pet. Please try again.");
    }
  };

  return (
    <div className="px-4 py-6 md:p-8 pb-10 font-nunito antialiased max-w-5xl mx-auto space-y-6">
      {/* Global Loader Overlay */}
      {(isUploading || isCreating) && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-6 flex items-center gap-3 shadow-xl">
            <Loader2 className="w-5 h-5 animate-spin text-mainBrandColor" />
            <span className="font-semibold text-sm">
              {isUploading ? "Uploading files..." : "Saving pet details..."}
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="border-b border-gray-300/40 pl-5 pb-4">
        <button
          onClick={() => navigate(-1)}
          className="group inline-flex items-center gap-1 text-xs font-bold text-gray-400 hover:text-logoHeading uppercase tracking-wider cursor-pointer transition-colors"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform stroke-[2.5]" />
          Back to Pets
        </button>

        <div>
          <h2 className="text-2xl font-black text-logoHeading tracking-tight">
            Add Pet
          </h2>
        </div>
      </div>

      {/* Section 1: Profiling */}
      <div className="bg-white px-4 md:px-6 py-5 rounded-2xl border border-mediumgray shadow-xs space-y-4">
        <p className="text-logoHeading text-lg font-black border-b border-gray-50 pb-2 mb-2 uppercase tracking-wide">
          Pet Information
        </p>

        {/* Assigned Category Selector with Tree Branch */}
        <div className="space-y-2 pt-1">
          <label className="text-xs font-bold text-slate-600 block">
            Assigned Categories <span className="text-red-500">*</span>
          </label>
          <div className="border border-slate-200 p-3 rounded-xl bg-slate-50/40 flex flex-wrap gap-2 min-h-[40px]">
            {selectedCategoryLabels.length === 0 && (
              <span className="text-xs text-slate-400 italic">
                No categories linked. Select from the tree below.
              </span>
            )}
            {selectedCategoryLabels.map((label, idx) => (
              <span
                key={idx}
                className="bg-emerald-50 text-emerald-800 px-2.5 py-1 rounded-lg text-[10px] font-black flex items-center gap-1.5 border border-emerald-100 uppercase tracking-wide"
              >
                {label}
                <button
                  type="button"
                  onClick={() =>
                    handleToggleCategorySelection(selectedCategoryIds[idx], label)
                  }
                  className="text-rose-500 hover:text-rose-700 font-bold text-xs cursor-pointer"
                >
                  ×
                </button>
              </span>
            ))}
          </div>

          <div className="relative w-full max-w-xs pt-1">
            <Search size={11} className="absolute left-3 top-3 text-slate-400" />
            <input
              type="text"
              placeholder="Search categories tree..."
              value={categorySearchQuery}
              onChange={(e) => setCategorySearchQuery(e.target.value)}
              className="w-full h-8 text-[11px] px-2 pl-7 border border-slate-200 rounded-lg outline-none focus:border-[#2F5E2E] bg-slate-50/50"
            />
          </div>

          <div className="border border-slate-200 rounded-xl p-3 max-h-52 overflow-y-auto bg-white shadow-inner">
            {isLoadingFullTree || isLoadingSystemTabs ? (
              <div className="flex items-center gap-1.5 text-slate-400 p-2 italic text-xs">
                <Loader2 size={13} className="animate-spin" /> Loading pet categories...
              </div>
            ) : petCategoryTree.length > 0 ? (
              renderHierarchicalTreeRows(petCategoryTree)
            ) : (
              <p className="text-slate-400 italic pl-1 text-xs">
                No categories found.
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 pt-2">
          <TextInputComp
            value={microchipId}
            onChangeText={setMicrochipId}
            label="Microchip ID"
            showLabel
            showStar
            placeholder="Enter Microchip Number"
          />
          <TextInputComp
            value={petName}
            onChangeText={setPetName}
            label="Pet Name"
            showLabel
            showStar
            placeholder="Enter Pet Name"
          />
          <TextInputComp
            value={breed}
            onChangeText={setBreed}
            label="Breed"
            showLabel
            placeholder="e.g. Golden Retriever"
            showStar
          />

          <div className="flex flex-col space-y-1">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">
              Date of Birth <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              required
              max={new Date().toISOString().split("T")[0]}
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              className="w-full border border-mediumgray bg-lightgray rounded-xl px-4 py-2.5 text-sm font-semibold transition focus:border-mainBrandColor outline-none focus:bg-white text-gray-700"
            />
          </div>

          <Dropdown
            options={genderOptions}
            value={gender}
            onChange={setGender}
            className="w-full"
            label="Gender"
            showLabel
            innerClassName="!py-2.5"
            showStar
          />
          <Dropdown
            options={typeOptions}
            value={petType}
            onChange={setPetType}
            className="w-full"
            label="Pet Type"
            showLabel
            innerClassName="!py-2.5"
            showStar
          />
          <Dropdown
            options={statusOptions}
            value={petStatus}
            onChange={setPetStatus}
            className="w-full"
            label="Status"
            showLabel
            innerClassName="!py-2.5"
            showStar
          />
          <TextInputComp
            value={color}
            onChangeText={setColor}
            label="Color"
            showLabel
            placeholder="e.g. Golden, White, Black"
          />
          <TextInputComp
            value={size}
            onChangeText={setSize}
            label="Size"
            showLabel
            placeholder="e.g. Small, Medium, Large"
          />
          <TextInputComp
            value={weight}
            onChangeText={setWeight}
            label="Weight (kg)"
            showLabel
            placeholder="e.g. 4.5"
          />
          <div className="md:col-span-2">
            <TextInputComp
              value={tag}
              onChangeText={setTag}
              label="Tag"
              showLabel
              placeholder="e.g. Most Active, Playful, Calm"
            />
          </div>

          <div className="md:col-span-2">
            <TextAreaComp
              value={description}
              onChangeText={setDescription}
              label="Pet Description"
              showLabel
              placeholder="Describe the pet, its personality, and other details..."
              showStar
            />
          </div>
        </div>
      </div>

      {/* Health & Checkbox Metrics */}
      <div className="bg-white px-4 md:px-6 py-5 rounded-2xl border border-mediumgray shadow-xs space-y-4">
        <p className="text-logoHeading text-lg font-black border-b border-gray-50 pb-2 mb-2 uppercase tracking-wide">
          Health Information
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-3 justify-center">
            <label className="flex items-center gap-3 cursor-pointer text-sm font-bold text-gray-600">
              <input
                type="checkbox"
                checked={isVaccinated}
                onChange={(e) => setIsVaccinated(e.target.checked)}
                className="w-4 h-4 rounded text-mainBrandColor focus:ring-mainBrandColor"
              />
              Vaccinated
            </label>
            <label className="flex items-center gap-3 cursor-pointer text-sm font-bold text-gray-600">
              <input
                type="checkbox"
                checked={isKCIRegistered}
                onChange={(e) => setIsKCIRegistered(e.target.checked)}
                className="w-4 h-4 rounded text-mainBrandColor focus:ring-mainBrandColor"
              />
              Is KCI Registered
            </label>
            <label className="flex items-center gap-3 cursor-pointer text-sm font-bold text-gray-600">
              <input
                type="checkbox"
                checked={isFeatured}
                onChange={(e) => setIsFeatured(e.target.checked)}
                className="w-4 h-4 rounded text-mainBrandColor focus:ring-mainBrandColor"
              />
              Show on Home Page
            </label>
          </div>
          {isVaccinated && (
            <TextAreaComp
              value={vaccinationDetails}
              onChangeText={setVaccinationDetails}
              label="Vaccination Details"
              showLabel
              placeholder="Enter vaccination details..."
            />
          )}
        </div>
      </div>

      {/* Section 2: Pricing Structure */}
      <div className="bg-white px-4 md:px-6 py-5 rounded-2xl border border-mediumgray shadow-xs space-y-4">
        <p className="text-logoHeading text-lg font-black border-b border-gray-50 pb-2 mb-2 uppercase tracking-wide">
          Pricing (AED)
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <TextInputComp
            value={price}
            onChangeText={setPrice}
            label="Price"
            showLabel
            showStar
            placeholder="e.g. 700"
          />
          <TextInputComp
            value={salePrice}
            onChangeText={setSalePrice}
            label="Sale Price"
            showLabel
            placeholder="e.g. 550"
          />
          <TextInputComp
            value={bookPrice}
            onChangeText={setBookPrice}
            label="Booking Amount"
            showLabel
            placeholder="e.g. 100"
          />
        </div>
      </div>

      {/* Section 3: Media Galleries */}
      <div className="bg-white px-4 md:px-6 py-5 rounded-2xl border border-mediumgray shadow-xs space-y-6">
        <p className="text-logoHeading text-lg font-black border-b border-gray-50 pb-2 mb-2 uppercase tracking-wide">
          Photos & Videos
        </p>

        <div className="w-full sm:w-1/3">
          <MediaUploadComp
            label="Main Image"
            value={mainImage}
            onChange={setMainImage}
            onFileSelect={handleMainImageUpload}
            showStar
          />
        </div>

        {/* Dynamic Sub-Images */}
        <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-black uppercase tracking-wider text-gray-400">
              Gallery Images
            </label>
            <button
              type="button"
              onClick={handleAddSubImageSlot}
              className="bg-logoHeading hover:opacity-90 text-white text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 transition cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" /> Add Image
            </button>
          </div>

          {subImages.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 bg-lightgray/40 border border-mediumgray rounded-2xl p-4">
              {subImages.map((imgUrl, idx) => (
                <div
                  key={idx}
                  className="relative group border border-gray-200 bg-white p-2 rounded-xl flex flex-col justify-between"
                >
                  <MediaUploadComp
                    label={`Gallery Image #${idx + 1}`}
                    value={imgUrl}
                    onChange={(val) =>
                      setSubImages((prev) => {
                        const next = [...prev];
                        next[idx] = val;
                        return next;
                      })
                    }
                    onFileSelect={(file) => handleSubImageUploadAtIndex(file, idx)}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveSubImageSlot(idx)}
                    className="mt-2 text-rose-600 hover:text-rose-800 text-xs font-bold flex items-center gap-1 self-end transition cursor-pointer"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove Slot
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 border border-dashed border-mediumgray rounded-2xl text-xs text-gray-400 font-semibold italic">
              No gallery images added yet. Click "Add Image" to upload.
            </div>
          )}
        </div>

        {/* Dynamic Videos */}
        <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-black uppercase tracking-wider text-gray-400">
              Pet Videos
            </label>
            <button
              type="button"
              onClick={handleAddVideoSlot}
              className="bg-logoHeading hover:opacity-90 text-white text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 transition cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" /> Add Video
            </button>
          </div>

          {videos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 bg-lightgray/40 border border-mediumgray rounded-2xl p-4">
              {videos.map((vidObj, idx) => (
                <div
                  key={idx}
                  className="relative group border border-gray-200 bg-white p-2 rounded-xl flex flex-col justify-between"
                >
                  <MediaUploadComp
                    label={`Video #${idx + 1}`}
                    value={vidObj.videoUrl}
                    onChange={(val) =>
                      setVideos((prev) => {
                        const next = [...prev];
                        next[idx] = { ...next[idx], videoUrl: val };
                        return next;
                      })
                    }
                    onFileSelect={(file) => handleVideoUploadAtIndex(file, idx)}
                    type="video"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveVideoSlot(idx)}
                    className="mt-2 text-rose-600 hover:text-rose-800 text-xs font-bold flex items-center gap-1 self-end transition cursor-pointer"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove Slot
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 border border-dashed border-mediumgray rounded-2xl text-xs text-gray-400 font-semibold italic">
              No videos added yet. Click "Add Video" to upload.
            </div>
          )}
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="mt-6 flex flex-col sm:flex-row items-center justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          disabled={isCreating || isUploading}
          className="w-full sm:w-auto text-center border border-mediumgray bg-white px-6 py-3 rounded-xl text-xs uppercase font-bold text-gray-500 hover:bg-lightgray transition tracking-wider disabled:opacity-40 cursor-pointer"
        >
          Cancel
        </button>
        <CommonButton
          text="Save Pet"
          onClick={handleFormSubmit}
          variant="filled"
          rounded="md"
          className="!w-full sm:!w-64"
          textContainerClassName="py-3 font-bold uppercase tracking-wider text-xs"
          disabled={isCreating || isUploading}
        />
      </div>
    </div>
  );
};

export default AdminPetAddScreen;